import { Path } from 'react-hook-form';

type ReportFieldPathNode = Record<string, unknown> | unknown[];

const isArrayIndexPathPart = (pathPart: string): boolean =>
  /^\d+$/.test(pathPart);

const isObjectContainer = (
  childValue: unknown,
): childValue is Record<string, unknown> =>
  typeof childValue === 'object' &&
  childValue !== null &&
  !Array.isArray(childValue) &&
  !(childValue instanceof Date);

const createNodeForNextPathPart = (
  nextPathPart: string,
): ReportFieldPathNode => (isArrayIndexPathPart(nextPathPart) ? [] : {});

const toReportFieldPathNode = (
  childValue: unknown,
): ReportFieldPathNode | null => {
  if (Array.isArray(childValue) || isObjectContainer(childValue)) {
    return childValue;
  }

  return null;
};

/**
 * Writes a CSV-mapped value into `reportFormValues` at the requested location.
 *
 * Expected format uses dots between levels, for example:
 * - "description"
 * - "location.latitude"
 * - "diagnosticProcedures.0.numberOfSick"
 *
 * If an intermediate part is missing (or has the wrong type), it is created:
 * - next part is numeric -> create an array
 * - otherwise -> create an object
 *
 * The function exits without writing when an array index is invalid
 * or when it cannot safely continue.
 */
export const setValueAtReportFieldPath = <T extends Record<string, unknown>>(
  reportFormValues: T,
  reportFieldPath: Path<T>,
  parsedCsvValue: unknown,
): void => {
  // Split the target field address by "." (example: "diagnosticProcedures.0.categoryOfTest").
  const pathParts = reportFieldPath.split('.');
  if (pathParts.length === 0) {
    return;
  }

  // Start from the root form object.
  let currentNode: ReportFieldPathNode = reportFormValues;

  // Walk level by level until the parent of the final value.
  for (
    let pathPartIndex = 0;
    pathPartIndex < pathParts.length - 1;
    pathPartIndex += 1
  ) {
    // Current address part.
    const pathPart = pathParts[pathPartIndex];
    // Next address part: used to infer object vs array.
    const nextPathPart = pathParts[pathPartIndex + 1];

    // Case 1: current value is an array.
    if (Array.isArray(currentNode)) {
      // Convert current part to an array index.
      const arrayIndex = Number(pathPart);
      // If not numeric, the address is invalid for an array.
      if (Number.isNaN(arrayIndex)) {
        return;
      }

      // Read the value at this index.
      const existingChildValue = currentNode[arrayIndex];
      // If next part is numeric, child must be an array.
      const nextPathPartTargetsArray = isArrayIndexPathPart(nextPathPart);
      // Check that child type matches what is expected next.
      const hasExpectedNodeType = nextPathPartTargetsArray
        ? Array.isArray(existingChildValue)
        : isObjectContainer(existingChildValue);

      // If missing or wrong type, replace with an empty value of the expected type.
      if (!hasExpectedNodeType) {
        currentNode[arrayIndex] = createNodeForNextPathPart(nextPathPart);
      }

      // Re-read child and confirm it is still traversable.
      const nextChildNode = toReportFieldPathNode(currentNode[arrayIndex]);
      // Safety guard: stop if we cannot continue safely.
      if (!nextChildNode) {
        return;
      }
      // Move to the next level.
      currentNode = nextChildNode;

      // Continue with the next part.
      continue;
    }

    // Case 2: current value is an object.
    // Read child value by key.
    const existingChildValue = currentNode[pathPart];
    // If next part is numeric, child must be an array.
    const nextPathPartTargetsArray = isArrayIndexPathPart(nextPathPart);
    // Check that child type is correct.
    const hasExpectedNodeType = nextPathPartTargetsArray
      ? Array.isArray(existingChildValue)
      : isObjectContainer(existingChildValue);

    // If missing or wrong type, initialize with expected container type.
    if (!hasExpectedNodeType) {
      currentNode[pathPart] = createNodeForNextPathPart(nextPathPart);
    }

    // Re-read child and confirm we can continue.
    const nextChildNode = toReportFieldPathNode(currentNode[pathPart]);
    // Safety guard: stop if traversal cannot continue safely.
    if (!nextChildNode) {
      return;
    }
    // Move to the next level.
    currentNode = nextChildNode;
  }

  // Last part: exact location where the value must be written.
  const leafPathPart = pathParts[pathParts.length - 1];
  // If parent is an array, last part must be a numeric index.
  if (Array.isArray(currentNode)) {
    // Convert last part to an index.
    const arrayIndex = Number(leafPathPart);
    // If not numeric, assignment is invalid.
    if (Number.isNaN(arrayIndex)) {
      return;
    }
    // Write the CSV value at the resolved index.
    currentNode[arrayIndex] = parsedCsvValue;
    return;
  }

  // Otherwise parent is an object: write by key.
  currentNode[leafPathPart] = parsedCsvValue;
};

/**
 * Generic function to transform a flat Record<string, CsvMappedValue> into type T.
 *
 * @param flatObject - The flat object with dot-notation paths as keys and CsvMappedValue as values
 * @returns The transformed object of type T
 */
export const transformFlatObjectToTyped = <T extends Record<string, unknown>>(
  flatObject: Record<Path<T>, unknown>,
): T => {
  const result = {} as T;

  Object.entries(flatObject).forEach(([path, value]) => {
    setValueAtReportFieldPath(result, path as Path<T>, value); // TypeScript not able to auto infer Path<T> here, type is transformed to string because it's too complex for him...
  });

  return result;
};
